import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import api from "@/lib/api";
import { Award, Download, CheckCircle2, XCircle, ClipboardCheck, ArrowRight, Loader2 } from "lucide-react";

export default function CertificatePage() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/certificate/status");
      setStatus(data);
      setLoading(false);
    })();
  }, []);

  async function download() {
    setDownloading(true);
    try {
      const res = await api.get("/certificate/download", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "DW-Training-Academy-Certificate.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert("Certificate not available yet — complete all modules and the practice exam first.");
    } finally {
      setDownloading(false);
    }
  }

  if (loading) return <AppLayout><div className="p-12 text-neutral-500">Loading certificate status…</div></AppLayout>;

  const eligible = status?.eligible;
  const moduleCount = Object.keys(status?.modules || {}).length;
  const passedCount = Object.values(status?.modules || {}).filter((m) => m.passed).length;
  const overallPct = Math.round(((passedCount + (status?.exam?.passed ? 1 : 0)) / (moduleCount + 1)) * 100);

  return (
    <AppLayout>
      <div className="max-w-4xl mx-auto px-6 py-10" data-testid="certificate-page">
        <div className="overline mb-3">Certification</div>
        <h1 className="font-serif text-5xl text-[var(--dw-navy)] mb-4 tracking-tight leading-tight">
          Certificate of Completion
        </h1>
        <p className="text-neutral-700 leading-relaxed max-w-2xl mb-10">
          Pass all five module quizzes at 85% and the full mock exam at 75% or higher to earn your
          signed certificate — a formal record of your DW Training Academy preparation.
        </p>

        {/* Hero card */}
        <div className={`dw-card p-8 md:p-10 relative overflow-hidden mb-8 ${eligible ? "border-[var(--dw-gold)]" : ""}`}>
          <div className="dw-grid-lines absolute inset-0 opacity-30" />
          <div className="relative flex flex-col md:flex-row items-start md:items-center gap-6">
            <div className={`w-20 h-20 flex items-center justify-center rounded-sm shrink-0 ${eligible ? "bg-[var(--dw-gold)]/15 border-2 border-[var(--dw-gold)]" : "bg-[var(--dw-navy)]/5 border border-[var(--dw-navy)]/15"}`}>
              <Award className={`w-10 h-10 ${eligible ? "text-[var(--dw-gold)]" : "text-neutral-400"}`} strokeWidth={1.5} />
            </div>
            <div className="flex-1">
              <div className="overline mb-1">{eligible ? "Certificate ready" : "Certificate progress"}</div>
              <div className="font-serif text-3xl md:text-4xl text-[var(--dw-navy)] mb-2 leading-tight">
                {eligible ? "You've earned it." : `${overallPct}% of requirements met`}
              </div>
              <div className="text-sm text-neutral-600" data-testid="cert-status-text">
                {eligible
                  ? "All five module quizzes passed at 85% and the mock exam at 75%. Download your signed certificate below."
                  : `${passedCount} of ${moduleCount} modules passed · Mock exam ${status?.exam?.passed ? "passed" : "pending"}. Keep going.`}
              </div>
              {eligible && (
                <button
                  onClick={download}
                  disabled={downloading}
                  data-testid="download-certificate"
                  className="mt-5 dw-btn-gold rounded-sm px-6 py-3 text-sm inline-flex items-center gap-2 disabled:opacity-60"
                >
                  {downloading ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} /> : <Download className="w-4 h-4" strokeWidth={1.5} />}
                  {downloading ? "Preparing…" : "Download certificate"}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Requirements checklist */}
        <div className="overline mb-3">Requirements</div>
        <div className="space-y-2">
          {[1, 2, 3, 4, 5].map((mid) => {
            const m = status?.modules?.[mid];
            const passed = m?.passed;
            return (
              <div key={mid} data-testid={`cert-req-module-${mid}`} className={`dw-card p-4 flex items-center gap-4 ${passed ? "border-emerald-300 bg-emerald-50/40" : ""}`}>
                {passed
                  ? <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" strokeWidth={1.5} />
                  : <div className="w-5 h-5 rounded-full border-2 border-neutral-300 shrink-0" />}
                <div className="flex-1">
                  <div className="text-sm font-medium text-[var(--dw-navy)]">Module {mid} — Knowledge Check at 85%+</div>
                  <div className="text-xs text-neutral-500">
                    {m?.best_score != null ? `Best: ${m.best_score}%` : "Not yet attempted"}
                  </div>
                </div>
                {!passed && (
                  <Link
                    to={`/modules/${mid}/quiz`}
                    className="text-xs text-[var(--dw-navy)] font-medium hover:text-[var(--dw-gold-soft)] flex items-center gap-1"
                    data-testid={`cert-take-module-${mid}`}
                  >
                    Take quiz <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
                  </Link>
                )}
              </div>
            );
          })}
          <div data-testid="cert-req-exam" className={`dw-card p-4 flex items-center gap-4 ${status?.exam?.passed ? "border-emerald-300 bg-emerald-50/40" : ""}`}>
            {status?.exam?.passed
              ? <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" strokeWidth={1.5} />
              : <div className="w-5 h-5 rounded-full border-2 border-neutral-300 shrink-0" />}
            <div className="flex-1">
              <div className="text-sm font-medium text-[var(--dw-navy)]">100-Question Practice Exam at 75%+</div>
              <div className="text-xs text-neutral-500">
                {status?.exam?.best_score != null ? `Best: ${status.exam.best_score}%` : "Not yet attempted"}
              </div>
            </div>
            {!status?.exam?.passed && (
              <Link
                to="/practice-exam"
                className="text-xs text-[var(--dw-navy)] font-medium hover:text-[var(--dw-gold-soft)] flex items-center gap-1"
                data-testid="cert-take-exam"
              >
                Take exam <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
              </Link>
            )}
          </div>
        </div>

        <div className="mt-10 text-xs text-neutral-500 leading-relaxed">
          <strong>About the certificate:</strong> Issued as a signed, dated PDF with a unique verification ID.
          This is a completion record from the DW Training Academy — not an NMLS-issued license or endorsement.
          To become a licensed MLO, complete the SAFE MLO National Test and MU4 application via NMLS.
        </div>
      </div>
    </AppLayout>
  );
}
